import { useForm } from '@tanstack/react-form';
import { useState } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { PrimaryButton } from '~/_shared/design-system/ui/atoms/button/primary/primary-button';
import { Typography } from '~/_shared/design-system/ui/atoms/typography/typography';
import { LabeledInput } from '~/_shared/design-system/ui/molecules/text-input/labeled-input/labeled-input';
import { LabeledPasswordInput } from '~/_shared/design-system/ui/molecules/text-input/labeled-password-input/labeled-password-input';

import './login-form.css';

const SUBMIT_LABEL = 'Sign In';
const SUBMITTING_LABEL = 'Signing in…';

interface LoginFormValues {
	username: string;
	password: string;
}

type LoginFormSubmitResult = { ok: true } | { ok: false; error: string };

interface LoginFormProps {
	on_submit: (values: LoginFormValues) => Promise<LoginFormSubmitResult>;
	id?: string;
	className?: string;
}

function validateUsername({ value }: { value: string }) {
	return value.trim().length === 0 ? 'Username is required.' : undefined;
}

function validatePassword({ value }: { value: string }) {
	return value.length === 0 ? 'Password is required.' : undefined;
}

const LoginForm = ({
	on_submit,
	id = 'LoginForm',
	className,
}: LoginFormProps) => {
	const [submit_error, setSubmitError] = useState<string | null>(null);

	const form = useForm({
		defaultValues: { username: '', password: '' } as LoginFormValues,
		onSubmit: async ({ value }) => {
			setSubmitError(null);
			const result = await on_submit(value);
			if (!result.ok) {
				setSubmitError(result.error);
			}
		},
	});

	return (
		<form
			id={id}
			noValidate
			className={classlist('login-form', className)}
			onSubmit={(e) => {
				e.preventDefault();
				void form.handleSubmit();
			}}
		>
			{submit_error ? (
				<Typography id={`${id}-error`} variant='error'>
					{submit_error}
				</Typography>
			) : null}

			<form.Field
				name='username'
				validators={{
					onChange: validateUsername,
					onSubmit: validateUsername,
				}}
			>
				{(field) => (
					<LabeledInput
						id={`${id}-username`}
						label='Username'
						autoComplete='username'
						required
						value={field.state.value}
						onChange={(e) => field.handleChange(e.target.value)}
						onBlur={field.handleBlur}
						is_invalid={field.state.meta.errors.length > 0}
					/>
				)}
			</form.Field>

			<form.Field
				name='password'
				validators={{
					onChange: validatePassword,
					onSubmit: validatePassword,
				}}
			>
				{(field) => (
					<LabeledPasswordInput
						id={`${id}-password`}
						label='Password'
						autoComplete='current-password'
						required
						value={field.state.value}
						onChange={(e) => field.handleChange(e.target.value)}
						onBlur={field.handleBlur}
						is_invalid={field.state.meta.errors.length > 0}
					/>
				)}
			</form.Field>

			<form.Subscribe
				selector={(state) => ({
					canSubmit: state.canSubmit,
					isSubmitting: state.isSubmitting,
				})}
			>
				{({ canSubmit, isSubmitting }) => (
					<PrimaryButton
						id={`${id}-submit`}
						type='submit'
						disabled={!canSubmit}
						is_loading={isSubmitting}
					>
						{isSubmitting ? SUBMITTING_LABEL : SUBMIT_LABEL}
					</PrimaryButton>
				)}
			</form.Subscribe>
		</form>
	);
};

export type { LoginFormProps, LoginFormSubmitResult, LoginFormValues };
export { LoginForm };
